import { editUserDetails } from '@api/user.js';
import Popover from '@components/Common/Popover.js';
import JoinCreateOrgModal from '@components/Modals/JoinCreateOrgModal.js';
import { STATICS_DIR } from '@constants/global.js';
import UserState from '@states/User.js';
import { UIButton, UIDiv, UIImage } from '@ui';

export default class OrganisationSwitcher extends UIButton {
  constructor({ orgs, placement }) {
    super();
    this.add(
      new UIImage(STATICS_DIR + 'images/logos/bee_dark.svg').setClass(
        'w-10 h-10',
      ),
    );

    this.items = [];
    this.placement = placement;
    this.itemElements = new Map(); // Store references to item elements

    // Process initial orgs if provided
    if (orgs && orgs.length > 0) {
      this.updateOrgs(orgs);
    }

    // Subscribe to UserState changes to update selection when selectedOrg changes
    UserState.subscribe(user => {
      if (user?.selectedOrg && this.items.length > 0) {
        this.setSelectedItem(user.selectedOrg);
      }
    });
    this.render();
  }

  render() {
    this.onClick(async e => {
      e.stopPropagation();

      // Refresh the content to ensure it reflects current state
      this.renderItems();

      Popover.show({
        trigger: this,
        content: this.content,
        placement: this.placement,
      });
    });
  }

  renderItems() {
    this.content = new UIDiv().addClass('p-4 flex flex-col');

    this.items.forEach(item => {
      const listItem = new UIButton(item.label);
      listItem.setClass('drop-down-item');
      listItem.onClick(() => {
        this.clearSelection();
        listItem.setClass('drop-down-item selected');
        Popover.hide();
        this.onSelect(item);
      });

      this.itemElements.set(item.value, listItem);
      this.content.add(listItem);
    });

    // Apply current selection state after rendering items
    if (UserState.user?.selectedOrg) {
      this.setSelectedItem(UserState.user.selectedOrg);
    }
  }

  setItems(items) {
    this.items = items;
    this.itemElements.clear();
    this.renderItems();
  }

  // Method to update organizations from parent component
  setOrganizations(orgs) {
    this.updateOrgs(orgs);
  }

  updateOrgs(orgs) {
    const processedOrgs = orgs.map(org => ({
      label: org.name,
      value: org.organizationId,
    }));

    // TODO: use IconButton
    processedOrgs.push({
      label: '➕ Join/Add',
      value: 'join-org',
      isJoinOrgButton: true,
      className:
        'join-org-btn flex items-center gap-2 text-blue-600 hover:bg-blue-50 px-4 py-2 rounded-md text-sm font-medium w-full',
      icon: 'plus-rounded',
    });

    this.setItems(processedOrgs);
    if (UserState.user?.selectedOrg) {
      this.setSelectedItem(
        processedOrgs?.filter(
          org => org.value === UserState.user.selectedOrg,
        )?.[0]?.value ||
          (processedOrgs.length > 1 ? processedOrgs[0]?.value : null),
      );
    }
  }

  clearSelection() {
    this.itemElements.forEach(element => {
      element.setClass('drop-down-item');
    });
  }

  setSelectedItem(value) {
    const selectedElement = this.itemElements.get(value);
    if (selectedElement) {
      this.clearSelection();
      selectedElement.setClass('drop-down-item selected');
    }
  }

  async onSelect(item) {
    if (item.isJoinOrgButton) {
      this.showJoinCreateModal();
      return;
    }

    // Check if we're already in the selected org
    if (UserState.user?.selectedOrg === item.value) {
      return; // Don't trigger anything if already selected
    }

    try {
      await editUserDetails({
        selectedOrg: item.value,
      });
      UserState.setUser({
        ...UserState.user,
        selectedOrg: item.value,
      });
    } catch (error) {
      console.error('Failed to update selected organization:', error);
    }
  }

  async showJoinCreateModal() {
    const modal = new JoinCreateOrgModal();
    const result = await modal.show();

    // Emit an event or callback to parent component to handle organization refresh
    if (result && this.onOrganizationChange) {
      this.onOrganizationChange();
    }
  }
}
